import React from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DeliveryDatePicker } from '@/components/ui/delivery-date-picker';
import StakeholderInput from '@/components/StakeholderInput';
import { orderTotals } from './itemPricing';

/** Label cell + value cell, the same dense grid used everywhere else. */
function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] items-stretch border-b border-slate-200 last:border-b-0">
      <div className="flex items-center bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-700 border-r border-slate-200">
        {label}
      </div>
      <div className="px-1.5 py-0.5 flex items-center min-w-0">{children}</div>
    </div>
  );
}

const CELL = 'h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-1';

/**
 * The order's own facts and its money, down the right-hand side — dates and
 * references above, the running total below, both visible while items are being
 * entered rather than hidden behind a collapsed "optional" section.
 */
export default function OrderStrip({
  orderDetails, setOrderDetails,
  expectedDeliveryDate, setExpectedDeliveryDate,
  stakeholderTags, setStakeholderTags,
  itemsTotal, entryDate,
  // an advance is taken once, when the order is booked; afterwards money arrives
  // as payments against the order, so editing one does not ask for it again
  showAdvance = true,
  showStakeholders = true,
  showMoney = true,
}) {
  const set = (patch) => setOrderDetails(d => ({ ...d, ...patch }));
  const { discount, gst, total, advance } = orderTotals(itemsTotal, orderDetails);

  return (
    <div className="space-y-4">
      <section className="border border-slate-200 rounded">
        <header className="bg-brand-blue px-3 py-1.5 rounded-t">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">Order</h3>
        </header>
        <Row label="Entry Date">
          <span className="px-1 text-sm text-slate-500 tabular-nums">{entryDate}</span>
        </Row>
        <Row label="PO / Ref No.">
          <Input value={orderDetails.po_ref_number} onChange={e => set({ po_ref_number: e.target.value })}
            placeholder="Customer's PO number" className={CELL} data-testid="po-ref-input" />
        </Row>
        <Row label="PO Date">
          <Input type="date" value={orderDetails.po_date} onChange={e => set({ po_date: e.target.value })}
            className={CELL} data-testid="po-date-input" />
        </Row>
        {/* the order's own promised day; a line item that is wanted sooner
            carries its own date in the grid */}
        <Row label="Delivery Date">
          <div className="w-full [&_button]:h-7 [&_button]:text-sm">
            <DeliveryDatePicker value={expectedDeliveryDate} onChange={setExpectedDeliveryDate} />
          </div>
        </Row>
        <Row label="Priority">
          <Select value={orderDetails.priority} onValueChange={v => set({ priority: v })}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['low', 'medium', 'high'].map(p => (
                <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>
        <Row label="Source">
          <Select value={orderDetails.order_source} onValueChange={v => set({ order_source: v })}>
            <SelectTrigger className="h-7 text-xs" data-testid="order-source-select">
              <SelectValue placeholder="How it came in" />
            </SelectTrigger>
            <SelectContent>
              {[['phone_call', 'Phone call'], ['whatsapp', 'WhatsApp'], ['email', 'Email'], ['walk_in', 'Walk-in']]
                .map(([value, label]) => <SelectItem key={value} value={value} className="text-xs">{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Row>
        {showStakeholders && (
          <Row label="Also involved">
            <div className="w-full [&_label]:hidden">
              <StakeholderInput value={stakeholderTags} onChange={setStakeholderTags} />
            </div>
          </Row>
        )}
      </section>

      {showMoney && (
      <section className="border border-slate-200 rounded">
        <header className="bg-brand-blue px-3 py-1.5 rounded-t">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">Amount</h3>
        </header>
        <div className={`grid ${showAdvance ? 'grid-cols-3' : 'grid-cols-2'} border-b border-slate-200`}>
          {[
            ['Disc %', 'discount_pct'],
            ['GST %', 'gst_pct'],
            ...(showAdvance ? [['Advance', 'advance_payment']] : []),
          ].map(([label, key], i) => (
            <div key={key} className={i > 0 ? 'border-l border-slate-200' : ''}>
              <p className="bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-600 border-b border-slate-200">
                {label}
              </p>
              <Input type="number" value={orderDetails[key] ?? ''}
                onChange={e => set({ [key]: e.target.value })} placeholder="0"
                className="h-7 text-sm text-right tabular-nums border-0 shadow-none focus-visible:ring-1 px-1.5" />
            </div>
          ))}
        </div>
        <dl className="text-sm">
          {[
            ['Items', itemsTotal],
            ['Discount', -discount],
            ['GST', gst],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between px-2.5 py-1 border-b border-slate-100">
              <dt className="text-slate-500 text-xs">{label}</dt>
              <dd className="tabular-nums text-slate-700">
                {value < 0 ? '−' : ''}₹{Math.abs(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </dd>
            </div>
          ))}
          <div className="flex justify-between px-2.5 py-1.5 bg-slate-50 border-t-2 border-brand-blue">
            <dt className="font-semibold text-slate-700">Total</dt>
            <dd className="font-bold tabular-nums text-brand-blue" data-testid="order-total">
              ₹{total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </dd>
          </div>
          {advance > 0 && (
            <div className="flex justify-between px-2.5 py-1 text-xs">
              <dt className="text-slate-500">Balance after advance</dt>
              <dd className="tabular-nums text-slate-700">
                ₹{(total - advance).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </dd>
            </div>
          )}
        </dl>
      </section>
      )}
    </div>
  );
}
